import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { type MembershipRequestView, type MyInvitationLookup } from '@apm/contracts';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorState, Skeleton } from '../../components/ui/states';
import { invitationApi, invitationKeys } from '../../features/projects/api';
import {
  InvitationDetail,
  InvitationSummary,
} from '../../features/projects/components/my-invitations';
import { displayDateTime } from '../../lib/date';
import { errorMessage } from '../../lib/error-message';

/*
  `request` là uuid — chuỗi mà `JSON.parse` của router không đọc nổi, nên schema chuỗi
  đúng ở đây (xem bẫy search param ở CLAUDE.md).
*/
const InvitationsSearch = z.object({ request: z.uuid().optional().catch(undefined) });

export const Route = createFileRoute('/_app/invitations')({
  validateSearch: InvitationsSearch,
  component: InvitationsPage,
});

/**
 * Lời mời vào dự án của tôi — APM-INVOPEN. Đích của thông báo mời.
 *
 * ## Vì sao một trang riêng, khi đã có khối lời mời ở trang chủ
 *
 * Thông báo mời từng dẫn về `/`, nơi khối `MyInvitations` đứng. Nhưng trang chủ là bảng
 * của người điều hành và **đá nhân viên sang "Việc của tôi"** — đúng nhóm hay được mời
 * nhất. Kết quả ở prod (dự án VOTE, 22/10): người được mời bấm thông báo, trang nháy
 * sang "Việc của tôi", và không có chỗ nào để nhận lời.
 *
 * Trang này không phụ thuộc vai: ai đăng nhập cũng mở được, và nó chỉ nói về lời mời gửi
 * cho người trong token.
 *
 * ## Mọi kết cục phải có chữ
 *
 * Thông báo sống lâu hơn lời mời: tới lúc bấm, lời mời có thể đã nhận (ở tab khác), đã từ
 * chối, đã bị thu hồi, hay PM đã thêm tay. Mỗi kết cục một câu nói rõ — một trang trống là
 * chính lỗi mà trang này sinh ra để sửa.
 */
function InvitationsPage(): ReactNode {
  const { t } = useTranslation();
  const { request } = Route.useSearch();

  const pending = useQuery({ queryKey: invitationKeys.mine, queryFn: invitationApi.mine });
  const others = (pending.data ?? []).filter((row) => row.id !== request);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">{t('invitations.page.title')}</h1>
        <p className="text-xs text-muted-foreground">{t('invitations.page.subtitle')}</p>
      </header>

      {request ? <RequestedInvitation requestId={request} /> : null}

      {pending.isPending ? (
        <Skeleton className="h-32 w-full" />
      ) : pending.isError ? (
        <ErrorState
          message={errorMessage(pending.error, t)}
          onRetry={() => void pending.refetch()}
        />
      ) : others.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label={t('invitations.page.othersTitle')}>
          {request ? (
            <h2 className="text-sm font-medium">{t('invitations.page.othersTitle')}</h2>
          ) : null}
          {others.map((invitation) => (
            <InvitationCard key={invitation.id}>
              <InvitationDetail invitation={invitation} onDone={() => undefined} />
            </InvitationCard>
          ))}
        </section>
      ) : request ? null : (
        <EmptyState
          title={t('invitations.page.emptyTitle')}
          description={t('invitations.page.emptyDescription')}
        />
      )}
    </div>
  );
}

/** Lời mời mà thông báo trỏ tới, ở mọi trạng thái. */
function RequestedInvitation({ requestId }: { requestId: string }): ReactNode {
  const { t } = useTranslation();
  const lookup = useQuery({
    queryKey: invitationKeys.one(requestId),
    queryFn: () => invitationApi.one(requestId),
  });

  if (lookup.isPending) return <Skeleton className="h-40 w-full" />;
  if (lookup.isError) {
    return (
      <ErrorState message={errorMessage(lookup.error, t)} onRetry={() => void lookup.refetch()} />
    );
  }

  const { invitation } = lookup.data;
  if (!invitation) {
    return (
      <EmptyState
        title={t('invitations.page.notFoundTitle')}
        description={t('invitations.page.notFoundDescription')}
      />
    );
  }

  const outcome = outcomeOf(invitation, lookup.data.alreadyMember);

  return (
    <InvitationCard>
      {outcome === 'answerable' ? (
        <InvitationDetail invitation={invitation} onDone={() => undefined} />
      ) : (
        <>
          <InvitationSummary invitation={invitation} />
          <Outcome outcome={outcome} invitation={invitation} member={lookup.data.alreadyMember} />
        </>
      )}
    </InvitationCard>
  );
}

export type InvitationOutcome =
  'answerable' | 'awaitingPm' | 'alreadyMember' | 'accepted' | 'declined' | 'withdrawn';

/**
 * Lời mời này đang ở đâu, nhìn từ người được mời. Thứ tự có nghĩa: "đã ở trong dự án"
 * thắng "đang chờ" — PM thêm tay trong lúc lời mời còn treo thì không còn gì để quyết.
 */
export function outcomeOf(
  invitation: MembershipRequestView,
  alreadyMember: MyInvitationLookup['alreadyMember'],
): InvitationOutcome {
  if (invitation.status === 'pending') {
    if (alreadyMember) return 'alreadyMember';
    // Chiều `user` (đăng ký qua link): người ký sau cùng là PM, không phải mình.
    return invitation.initiatedBy === 'pm' ? 'answerable' : 'awaitingPm';
  }
  return invitation.status;
}

function Outcome({
  outcome,
  invitation,
  member,
}: {
  outcome: Exclude<InvitationOutcome, 'answerable'>;
  invitation: MembershipRequestView;
  /** Đang là thành viên dự án — chỉ khi ấy mới có nút mở dự án. */
  member: boolean;
}): ReactNode {
  const { t } = useTranslation();
  // Đơn qua link do PM quyết, lời mời do mình quyết — câu chữ theo người ký sau cùng.
  const side = invitation.initiatedBy === 'pm' ? 'invite' : 'application';
  const at = invitation.decidedAt ? displayDateTime(invitation.decidedAt) : '';

  return (
    <div
      role="status"
      className="flex flex-col gap-2 rounded-DEFAULT bg-muted/40 px-3 py-2 text-sm"
    >
      <p className="font-medium">
        {outcome === 'accepted' || outcome === 'declined'
          ? t(`invitations.page.outcome.${side}.${outcome}`, { at })
          : t(`invitations.page.outcome.${outcome}`)}
      </p>
      {outcome === 'declined' && invitation.note ? (
        <p className="text-muted-foreground">
          {t('invitations.page.reason', { note: invitation.note })}
        </p>
      ) : null}
      {member ? (
        <div>
          <Button size="sm" asChild>
            <Link to="/projects" search={{ projectId: invitation.projectId }}>
              {t('invitations.page.openProject')}
            </Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function InvitationCard({ children }: { children: ReactNode }): ReactNode {
  return (
    <article className="flex flex-col gap-3 rounded-DEFAULT border border-rag-amber/40 bg-card p-4">
      {children}
    </article>
  );
}
